/**
 * Brokerage "bloodline" helpers — pure, DB-free, client-side.
 *
 * The CEO feedback (ClickUp 86exgvyfa) asks for a green progress border around
 * property and owner cards representing how much of an ACTIVE brokerage contract
 * term has elapsed (start → end as a %), and a dashboard counter of properties
 * currently under brokerage. All of that is derived here from the brokerage
 * contract list the client already caches — no new persistent data, no API call.
 *
 * Status rules mirror the Nest status engine
 * (apps/api/src/brokerage-contracts/status-engine.ts):
 *   - `pending` / `renew` are explicit stored states, honoured as-is.
 *   - otherwise the status is derived from `end_date` vs `now`:
 *       expired  — end_date is in the past
 *       expiring — still valid but within EXPIRING_WINDOW_DAYS of expiry
 *       active   — valid with more than EXPIRING_WINDOW_DAYS left
 * A card shows a bloodline only while its contract is `active` or `expiring`
 * (still in force). Expired / pending / renew contracts get no bloodline.
 */
import type { BrokerageContract } from "@dallali/shared";

const MS_PER_DAY = 86_400_000;

/** Days before `end_date` at which an active contract is flagged "expiring" (mirror of the engine's Q1 window). */
export const EXPIRING_WINDOW_DAYS = 60;

export type EffectiveBrokerageStatus =
  | "pending"
  | "active"
  | "expiring"
  | "expired"
  | "renew";

/** Whole-day difference (b − a) on the UTC calendar — matches the status engine. */
function dayDiff(a: number, b: number): number {
  const da = new Date(a);
  const db = new Date(b);
  const ua = Date.UTC(da.getUTCFullYear(), da.getUTCMonth(), da.getUTCDate());
  const ub = Date.UTC(db.getUTCFullYear(), db.getUTCMonth(), db.getUTCDate());
  return Math.round((ub - ua) / MS_PER_DAY);
}

/**
 * Derive the effective status of one contract relative to `now`.
 * `now` is a millisecond timestamp (default Date.now()) — passed in by callers
 * that memoise so renders stay deterministic.
 */
export function effectiveBrokerageStatus(
  contract: Pick<BrokerageContract, "status" | "end_date">,
  now: number = Date.now(),
): EffectiveBrokerageStatus {
  if (contract.status === "pending" || contract.status === "renew") {
    return contract.status;
  }
  const end = new Date(contract.end_date).getTime();
  if (Number.isNaN(end)) return "active";
  const daysToEnd = dayDiff(now, end);
  if (daysToEnd < 0) return "expired";
  if (daysToEnd <= EXPIRING_WINDOW_DAYS) return "expiring";
  return "active";
}

/** A contract drives a bloodline only while it is still in force. */
export function isBloodlineActive(status: EffectiveBrokerageStatus): boolean {
  return status === "active" || status === "expiring";
}

/**
 * Fraction (0..1) of the contract term that has elapsed at `now`.
 * Clamped to [0, 1]; returns 0 for a malformed or zero-length term so a bad
 * date never produces a NaN-width border.
 */
export function elapsedFraction(
  startDate: string,
  endDate: string,
  now: number = Date.now(),
): number {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  const frac = (now - start) / (end - start);
  if (frac < 0) return 0;
  if (frac > 1) return 1;
  return frac;
}

/** Elapsed fraction as a whole-number percentage (0..100). */
export function elapsedPercent(
  startDate: string,
  endDate: string,
  now: number = Date.now(),
): number {
  return Math.round(elapsedFraction(startDate, endDate, now) * 100);
}
